import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { GlowCard } from "@/ui/components/glow-card";
import { PageFrame } from "@/components/kit/page-frame";
import { PillSwitch, StatPill } from "@/components/kit/pills";
import { Sparkline, StatCard } from "@/components/kit/stat-card";
import { SOURCE_OPTIONS, useDataSource } from "@/lib/data-source";
import { ProjectPicker } from "@/components/project-picker";
import { api, type MetricRow, type TimeseriesPoint } from "@/lib/api";
import { formatCost, formatDuration, formatNumber } from "@/lib/utils";
import { SeriesChart } from "./charts";
import { ProjectsView, RecentView, SessionsView } from "./views";

export const RANGES = { "1h": 3600, "6h": 21600, "24h": 86400, "7d": 604800, all: 0 } as const;
const BUCKET: Record<Range, number> = { "1h": 60, "6h": 300, "24h": 900, "7d": 3600, all: 300 };
type Range = keyof typeof RANGES;
export const VIEWS = ["overview", "projects", "sessions", "recent"] as const;
export type OverviewView = (typeof VIEWS)[number];
export type OverviewSearch = { range?: Range; project?: string; view?: OverviewView };

const LIVE = { refetchInterval: 10_000 };

/** Sum every series into one value per bucket. */
function totals(data: TimeseriesPoint[] | undefined) {
  const sums = new Map<number, number>();
  for (const d of data ?? []) sums.set(d.bucket, (sums.get(d.bucket) ?? 0) + d.value);
  return [...sums].sort((a, b) => a[0] - b[0]).map(([t, v]) => ({ t, v }));
}

function ChartPanel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <GlowCard className="min-w-0 p-5 sm:p-6">
      <h2 className="mb-4 text-base font-medium tracking-[-0.01em]">{title}</h2>
      {children}
    </GlowCard>
  );
}

export function OverviewPage() {
  const search = useSearch({ from: "/_app/" });
  const navigate = useNavigate({ from: "/" });
  const range = search.range ?? "all";
  const project = search.project;
  const requested = search.view ?? "overview";
  const view: OverviewView = project && requested === "projects" ? "overview" : requested;
  const since = RANGES[range] ? Math.floor(Date.now() / 1000) - RANGES[range] : 0;
  const { source, setSource } = useDataSource();
  const key = [range, project ?? "", source];

  const setSearch = (next: Partial<OverviewSearch>) =>
    navigate({ search: (prev) => ({ ...prev, ...next }), replace: true });

  const stats = useQuery({ queryKey: ["stats", ...key], queryFn: () => api.stats(since, project, source), ...LIVE });
  const tokens = useQuery({
    queryKey: ["ts-tokens", ...key],
    queryFn: () => api.timeseries("claude_code.token.usage", since, "type", BUCKET[range], project, source),
    ...LIVE,
  });
  const cost = useQuery({
    queryKey: ["ts-cost", ...key],
    queryFn: () => api.timeseries("claude_code.cost.usage", since, "model", BUCKET[range], project, source),
    ...LIVE,
  });
  const projects = useQuery({ queryKey: ["projects", range, source], queryFn: () => api.projects(since, source), ...LIVE });
  const sessions = useQuery({ queryKey: ["sessions", ...key], queryFn: () => api.sessions(since, project, source), ...LIVE });
  const recent = useQuery({
    queryKey: ["recent", ...key],
    queryFn: () => api.telemetry<MetricRow>("metrics", { project, source, limit: 20 }),
    ...LIVE,
  });

  const s = stats.data;
  const tokenSpark = useMemo(() => totals(tokens.data), [tokens.data]);
  const costSpark = useMemo(() => totals(cost.data), [cost.data]);

  const switcher = (
    <PillSwitch
      label="View"
      value={view}
      onChange={(v) => setSearch({ view: v === "overview" ? undefined : v })}
      options={[
        { value: "overview", label: "Overview" },
        ...(project ? [] : [{ value: "projects" as const, label: "Projects", count: projects.data?.length }]),
        { value: "sessions", label: "Sessions", count: sessions.data?.length },
        { value: "recent", label: "Recent" },
      ]}
    />
  );

  return (
    <PageFrame
      title={project ?? "Overview"}
      crumb={project}
      switcher={switcher}
      actions={
        <>
          <PillSwitch compact label="Data source" value={source} onChange={setSource} options={SOURCE_OPTIONS} />
          <ProjectPicker value={project} source={source} onChange={(p) => setSearch({ project: p })} />
        </>
      }
      footerStart={
        <PillSwitch
          label="Range"
          value={range}
          onChange={(r) => setSearch({ range: r === "all" ? undefined : r })}
          options={(Object.keys(RANGES) as Range[]).map((r) => ({ value: r, label: r === "all" ? "All" : r }))}
        />
      }
      footerEnd={
        <>
          <StatPill label="Pass" value={formatNumber(s?.routing.pass.c ?? 0)} />
          <StatPill label="Drop" value={formatNumber(s?.routing.drop.c ?? 0)} />
        </>
      }
    >
      {view === "overview" ? (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-5">
            <StatCard
              label="Tokens"
              value={formatNumber(s?.tokens.total ?? 0)}
              detail={`${formatNumber(s?.tokens.input ?? 0)} in · ${formatNumber(s?.tokens.output ?? 0)} out`}
              spark={<Sparkline id="spark-tokens" data={tokenSpark} />}
            />
            <StatCard
              label="Cost"
              value={formatCost(s?.cost.total ?? 0)}
              spark={<Sparkline id="spark-cost" data={costSpark} color="var(--chart-1)" />}
            />
            <StatCard label="Active time" value={formatDuration(s?.active_time.total ?? 0)} />
            <StatCard label="Lines changed" value={`+${Math.round(s?.lines.added ?? 0)} / −${Math.round(s?.lines.removed ?? 0)}`} />
            <StatCard
              label="Routing"
              value={`${formatNumber(s?.routing.pass.c ?? 0)} / ${formatNumber(s?.routing.drop.c ?? 0)}`}
              detail="pass / drop"
            />
          </div>
          <div className="grid gap-3 lg:grid-cols-2">
            <ChartPanel title="Tokens by type">
              <SeriesChart data={tokens.data ?? []} format={formatNumber} />
            </ChartPanel>
            <ChartPanel title="Cost by model">
              <SeriesChart data={cost.data ?? []} format={(v) => `$${v.toFixed(2)}`} />
            </ChartPanel>
          </div>
        </div>
      ) : view === "projects" ? (
        <ProjectsView rows={projects.data ?? []} onSelect={(p) => setSearch({ project: p, view: undefined })} />
      ) : view === "sessions" ? (
        <SessionsView rows={sessions.data ?? []} showProject={!project} />
      ) : (
        <RecentView rows={recent.data ?? []} />
      )}
    </PageFrame>
  );
}
